import DAYS from "../constants/days";
import {
  readScheduleRangeFromDto,
  readSlotGenerationModeFromDto,
} from "../constants/appointmentSchedule";

export const DAY_TO_ENUM = {
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
  SUNDAY: 7,
};

export const ENUM_TO_DAY = {
  0: null,
  1: "MONDAY",
  2: "TUESDAY",
  3: "WEDNESDAY",
  4: "THURSDAY",
  5: "FRIDAY",
  6: "SATURDAY",
  7: "SUNDAY",
};

/** Backend DayType (number or string) → uppercase day key e.g. "MONDAY". */
export function normalizeDayKey(day) {
  if (day == null) return "";
  if (typeof day === "number") {
    return ENUM_TO_DAY[day] || "";
  }
  const upper = String(day).toUpperCase();
  if (upper === "NONE" || upper === "0") return "";
  return DAYS.includes(upper) ? upper : upper;
}

export function dayKeyToEnum(dayKey) {
  return DAY_TO_ENUM[normalizeDayKey(dayKey)] ?? null;
}

const TIME_RANGE_RE = /^([01]?\d|2[0-3]):[0-5]\d$/;

/** Filter/sort `{ startHour, endHour }` rows for CalculateMultipleAvailableTimes. */
export function normalizeTimeRanges(ranges = []) {
  return (Array.isArray(ranges) ? ranges : [ranges])
    .map((range) => ({
      startHour: String(range?.startHour ?? "").trim(),
      endHour: String(range?.endHour ?? "").trim(),
    }))
    .filter(
      (range) =>
        TIME_RANGE_RE.test(range.startHour) &&
        TIME_RANGE_RE.test(range.endHour) &&
        range.startHour < range.endHour
    )
    .sort((a, b) => a.startHour.localeCompare(b.startHour));
}

export function normalizeAvailableTimes(times = []) {
  return (Array.isArray(times) ? times : [])
    .map((item) => {
      if (typeof item === "string") return { hour: item };
      const hour = item?.hour ?? item?.Hour;
      if (!hour) return null;
      return item?.id != null ? { id: item.id, hour } : { hour };
    })
    .filter(Boolean);
}

export function normalizeDoctorAvailableDaysDTO(data) {
  if (!data) {
    return {
      doctorId: null,
      appointmentScheduleRange: 30,
      appointmentSlotGenerationMode: 0,
      days: [],
    };
  }

  return {
    ...data,
    appointmentScheduleRange: readScheduleRangeFromDto(data),
    appointmentSlotGenerationMode: readSlotGenerationModeFromDto(data),
    days: (data.days || []).map((dayRecord) => ({
      ...dayRecord,
      day: normalizeDayKey(dayRecord.day),
      availableTimes: normalizeAvailableTimes(dayRecord.availableTimes),
    })).filter((dayRecord) => dayRecord.day),
  };
}

export function normalizeHourLabel(value) {
  const match = /^(\d{1,2}):(\d{2})/.exec(String(value ?? "").trim());
  if (!match) return "";
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return "";
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

export function getHourList(times = []) {
  return normalizeAvailableTimes(times)
    .map((t) => normalizeHourLabel(t.hour))
    .filter(Boolean)
    .sort();
}

export function areHourListsEqual(a = [], b = []) {
  const left = getHourList(a);
  const right = getHourList(b);
  return left.length === right.length && left.every((h, i) => h === right[i]);
}

export function getFirstApiErrorMessage(error) {
  const messages =
    error?.data?.error?.messages ||
    error?.error?.messages ||
    error?.data?.messages;

  if (messages && typeof messages === "object") {
    const values = Object.values(messages);
    if (values.length > 0) return values[0];
  }

  return null;
}

export function getApiErrorCode(error) {
  const messages =
    error?.data?.error?.messages ||
    error?.error?.messages ||
    error?.data?.messages;

  if (messages && typeof messages === "object") {
    const keys = Object.keys(messages);
    if (keys.length > 0) return keys[0];
  }

  return null;
}
